import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { MeasureKey, Phase } from "../../api/types";
import type { LineName, Side, UserCorridor } from "./corridorEdit";

/** Poignée du mode édition du fuseau (docs/14 §2.3) : sommet d'une limite, poignée de milieu entre les paires
 *  `index` et `index + 1`, extrémité de LIMA sur une limite, courbure de LIMA.
 *  Ajouts du paquet « carte » (additifs, sans effet sur l'existant) : `mid.side`, limite qui porte la poignée de
 *  milieu (une poignée par limite ; absente : les deux) ; `limaMid`, poignée de milieu entre les sommets `index`
 *  et `index + 1` du tracé d'une LIMA sélectionnée (extrémité gauche = 0) : la saisir insère une courbure.
 *  Lignes de la ZO (docs/15 §4.3) : `measureEnd`, extrémité d'une ligne mobile sur une limite (au temps `phase`
 *  pour la FLOT et la CFL) : la glisser incline la ligne. */
export type ManoeuvreHandle =
  | { kind: "vertex"; side: Side; index: number }
  | { kind: "mid"; index: number; side?: Side }
  | { kind: "limaEnd"; line: LineName; side: Side }
  | { kind: "limaBend"; line: LineName; index: number }
  | { kind: "limaMid"; line: LineName; index: number }
  | { kind: "measureEnd"; key: MeasureKey; phase: Phase | null; side: Side };

type ManoeuvreState = {
  /** Mode édition du fuseau ouvert (bandeau, poignées, clavier de la carte coupé). */
  editing: boolean;
  /** Géométrie en cours de geste, montrée à la place de celle du serveur jusqu'à sa réponse (règle B28). */
  working: UserCorridor | null;
  hover: ManoeuvreHandle | null;
  /** Poignée saisie pendant un glissement. */
  active: ManoeuvreHandle | null;
  selected: ManoeuvreHandle | null;
  /** LIMA sélectionnée : ses courbures et ses poignées de milieu s'affichent. */
  selectedLine: LineName | null;
  /** « Garder la largeur » : glisser un sommet déplace aussi son vis-à-vis. Mémorisé par poste. */
  keepWidth: boolean;
  setEditing: (on: boolean) => void;
  setWorking: (corridor: UserCorridor | null) => void;
  setHover: (handle: ManoeuvreHandle | null) => void;
  setActive: (handle: ManoeuvreHandle | null) => void;
  select: (handle: ManoeuvreHandle | null) => void;
  selectLine: (line: LineName | null) => void;
  setKeepWidth: (on: boolean) => void;
};

/** État partagé du mode édition du fuseau, entre la carte (poignées, gestes) et le panneau Manœuvre. Seule la
 *  préférence « Garder la largeur » survit au rechargement ; quitter l'édition efface le reste. */
export const useManoeuvre = create<ManoeuvreState>()(
  persist(
    (set) => ({
      editing: false,
      working: null,
      hover: null,
      active: null,
      selected: null,
      selectedLine: null,
      keepWidth: true,
      setEditing: (on) =>
        set(
          on
            ? { editing: true }
            : { editing: false, working: null, hover: null, active: null, selected: null, selectedLine: null },
        ),
      setWorking: (working) => set({ working }),
      setHover: (hover) => set({ hover }),
      setActive: (active) => set({ active }),
      select: (selected) => set({ selected }),
      selectLine: (selectedLine) => set({ selectedLine }),
      setKeepWidth: (keepWidth) => set({ keepWidth }),
    }),
    {
      name: "generex-manoeuvre",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ keepWidth: state.keepWidth }),
    },
  ),
);
